import { useCallback, useEffect, useMemo, useState } from 'react';

import { GetUnitReadingsRequestDTO, MODULE, PdfScope } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import DownloadIcon from '@/components/icons/download';
import UndoIcon from '@/components/icons/undo';
import CreateReadingLogDrawer from '@/components/reading-logs/create-reading-log-drawer';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageHeader, TablePageHeaderActionButton } from '@/components/table-page/table-page-header';
import { TablePageSelectedCountIndicator } from '@/components/table-page/table-page-selected-count-indicator';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { assetReadingLogKeys, getAssetReadingLogsInfiniteQueryOptions } from '@/queries/assets';
import { buildFilterFields } from '@/queries/column-filter';
import { useModulesStore } from '@/stores/modules.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';

import { NoReadingLogsCreatedDisplay, NoReadingLogsFoundFromFiltersDisplay, ReadingLogTable } from './table';
import { columns, FILTER_FIELD_MAP } from './table-meta';

function AssetReadingLogsPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const toast = useToast();

  const { setModuleFilters } = useModulesStore();

  const {
    config: { columnOrder, columnVisibility, sorting, filters },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.AssetReadingLogs, {
    filterFieldMap: FILTER_FIELD_MAP,
    columns,
  });

  const [createReadingLogDrawerOpen, setCreateReadingLogDrawerOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});

  const {
    data: readingLogsData,
    fetchNextPage: fetchNextReadingLogsPage,
    hasNextPage: hasNextReadingLogsPage,
    isLoading: isLoadingReadingLogs,
    isFetching: isFetchingReadingLogs,
    isFetchingNextPage: isFetchingNextReadingLogsPage,
    error: readingLogsError,
  } = useInfiniteQuery(
    getAssetReadingLogsInfiniteQueryOptions({
      filters: buildFilterFields(filters, FILTER_FIELD_MAP, {
        extraFields: { assetId: [id as string] },
      }),
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetUnitReadingsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : {
            field: 'createdAt',
            direction: 'desc',
          },
      enabled: !isLoadingPageConfig,
    }),
  );

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextReadingLogsPage,
    hasNextPage: hasNextReadingLogsPage,
    isFetching: isFetchingReadingLogs,
    isError: !!readingLogsError,
    root: scrollRoot,
  });

  useEffect(() => {
    if (readingLogsError) {
      toast.error(readingLogsError.message);
    }
  }, [toast, readingLogsError]);

  useResetRowSelectionOnChange(setSelectedRows, [filters, sorting.key, sorting.direction]);

  const flattenedData = useMemo(() => readingLogsData?.pages.flatMap((page) => page.data) ?? [], [readingLogsData]);

  const selectedReadingLogs = useMemo(() => {
    const ids = Object.keys(selectedRows);
    return ids.filter((rowId) => !isNaN(parseInt(rowId))).map((rowId) => flattenedData[parseInt(rowId)]);
  }, [selectedRows, flattenedData]);

  const { canExportCsv } = usePermissions();
  const buildReadingLogsExportRequest = useCallback((): RegisterExportQuery<'reading-logs'> => {
    return {
      queryType: 'reading-logs',
      filters: { fields: { id: selectedReadingLogs.map((log) => log.id) } },
      orderBy: sorting.key
        ? { field: sorting.key as NonNullable<GetUnitReadingsRequestDTO['orderBy']>['field'], direction: sorting.direction as 'asc' | 'desc' }
        : undefined,
      columns: activeExportColumnIds(columnOrder),
    };
  }, [columnOrder, selectedReadingLogs, sorting]);

  const handleDownload = useCallback(() => {
    openExportTab({ kind: 'csv', request: { ...buildReadingLogsExportRequest(), filename: buildCsvExportFilename('reading-logs') } });
  }, [buildReadingLogsExportRequest]);

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!readingLogsData?.pages) return;
    openExportTab(buildPdfExportJob({ scope, records: [], register: buildReadingLogsExportRequest() }));
  }

  const isLoading = isLoadingPageConfig || isLoadingReadingLogs;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <TablePageHeader>
        <div className="no-scrollbar w-full overflow-x-auto py-0.5">
          <div className="flex h-full min-w-[580px] items-center justify-between">
            <div className="flex h-full items-center gap-x-2 px-2">
              {filters && filters.length > 0 && (
                <TablePageHeaderActionButton
                  icon={<UndoIcon />}
                  label="Remove all Filters"
                  kind="secondary"
                  onClick={() => setModuleFilters(MODULE.AssetReadingLogs, [])}
                />
              )}
              {selectedReadingLogs.length > 0 && (
                <>
                  <TablePageSelectedCountIndicator selectedCount={selectedReadingLogs.length} totalCount={readingLogsData?.pages[0]?.totalCount ?? 0} />
                  {canExportCsv && <TablePageHeaderActionButton icon={<DownloadIcon />} label="Download CSV" onClick={handleDownload} />}
                  {canExportCsv && <DownloadPdfMenu onSelect={handlePdfScopeSelect} />}
                </>
              )}
            </div>

            <div className="mr-1 flex h-full items-center">
              <CreateReadingLogDrawer
                assetId={id}
                open={createReadingLogDrawerOpen}
                onOpenChange={(open) => {
                  if (!open) setCreateReadingLogDrawerOpen(false);
                }}
                callback={() => {
                  queryClient.invalidateQueries({ queryKey: assetReadingLogKeys.all });
                }}
              />
            </div>
          </div>
        </div>
      </TablePageHeader>

      <ScrollArea type="always" viewportRef={setScrollRoot} className="relative flex flex-1 flex-col">
        <ScrollBar orientation="horizontal" />
        <ReadingLogTable
          data={flattenedData}
          notFoundDisplay={
            isLoading ? null : filters && filters.length > 0 ? (
              <NoReadingLogsFoundFromFiltersDisplay removeColumnFilters={() => setModuleFilters(MODULE.AssetReadingLogs, [])} />
            ) : (
              <NoReadingLogsCreatedDisplay />
            )
          }
          loading={isLoading}
          onSortingChange={(sorting) =>
            updatePageConfig({
              table: { sorting },
            })
          }
          sorting={sorting}
          grouping={[]}
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          columnFilters={filters || []}
          onColumnFiltersChange={(filters) => setModuleFilters(MODULE.AssetReadingLogs, filters)}
          rowSelection={selectedRows}
          onRowSelectionChange={setSelectedRows}
        />
        {hasNextReadingLogsPage && (
          <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
            {isFetchingNextReadingLogsPage ? 'Loading more...' : 'Load Newer'}
          </Button>
        )}
      </ScrollArea>
    </div>
  );
}

export default AssetReadingLogsPage;
